/**
 * AssignmentsPanel.jsx
 *
 * Assign a zoom settings rule to specific WooCommerce product categories
 * and/or individual products.
 *
 * Architecture (mirrors FormSettingsPanel.jsx from the form builder):
 *  - No local Save button. PluginSettingsApp calls assignmentsSaveRef.current(id)
 *    inside handleSave, so assignments save atomically with the settings row.
 *  - assignmentsSaveRef.current receives the final id at call time — safe for
 *    new items that didn't have an id when the panel mounted.
 *  - Category tree shows a flat filtered list when the filter input is active.
 *  - Product search is debounced at 350 ms.
 */

import {
    useState,
    useEffect,
    useCallback,
    useRef,
    useMemo,
    createPortal,
} from "@wordpress/element";
import {
    Check,
    Search,
    X,
    Loader2,
    ChevronDown,
    ChevronRight,
    Tag,
    Package,
    AlertTriangle,
} from "lucide-react";
import {
    getAssignments,
    saveAssignments,
    getWcCategories,
    searchWcProducts,
    getWcProductsByIds,
} from "../../api/assignments";
import { ButtonGroup } from "../ui/index";

const APPLY_TO_OPTIONS = [
    { value: "all", label: "All Products" },
    { value: "selected", label: "Selected Only" },
];

// ── Helpers ───────────────────────────────────────────────────────────────────

const buildTree = (cats, parent = 0) =>
    cats
        .filter((c) => c.parent === parent)
        .map((c) => ({ ...c, children: buildTree(cats, c.id) }));

// ── Category tree node ────────────────────────────────────────────────────────

const CategoryNode = ({ node, selected, onToggle, depth = 0 }) => {
    const [expanded, setExpanded] = useState(depth < 1);
    const isSelected = selected.includes(node.id);
    const hasChildren = node.children?.length > 0;

    return (
        <div>
            <div
                className={[
                    "flex items-center gap-1.5 rounded-md py-[6px] cursor-pointer",
                    "transition-colors duration-100",
                    isSelected ? "bg-blue-50" : "hover:bg-gray-50",
                ].join(" ")}
                style={{
                    paddingLeft: `${10 + depth * 14}px`,
                    paddingRight: "10px",
                }}
                onClick={() => onToggle(node.id)}
            >
                {/* Expand/collapse chevron */}
                {hasChildren ? (
                    <button
                        className="h-4 w-4 shrink-0 flex items-center justify-center rounded text-gray-300 hover:text-gray-500 transition-colors"
                        onClick={(e) => {
                            e.stopPropagation();
                            setExpanded((v) => !v);
                        }}
                        aria-label={expanded ? "Collapse" : "Expand"}
                    >
                        {expanded ? (
                            <ChevronDown size={11} />
                        ) : (
                            <ChevronRight size={11} />
                        )}
                    </button>
                ) : (
                    <span className="w-4 shrink-0" />
                )}

                {/* Checkbox */}
                <span
                    className={[
                        "h-[15px] w-[15px] shrink-0 rounded-[3px] border-[1.5px]",
                        "flex items-center justify-center transition-colors duration-100",
                        isSelected
                            ? "border-blue-600 bg-blue-600"
                            : "border-gray-200 bg-white",
                    ].join(" ")}
                >
                    {isSelected && (
                        <Check
                            size={9}
                            strokeWidth={3}
                            className="text-white"
                        />
                    )}
                </span>

                {/* Label */}
                <span
                    className={[
                        "flex-1 min-w-0 truncate text-[12px] leading-none transition-colors duration-100",
                        isSelected
                            ? "font-medium text-blue-600"
                            : "text-gray-600",
                    ].join(" ")}
                >
                    {node.name}
                </span>

                {/* Count */}
                {node.count > 0 && (
                    <span className="shrink-0 tabular-nums text-[10px] text-gray-300">
                        {node.count}
                    </span>
                )}
            </div>

            {hasChildren && expanded && (
                <div>
                    {node.children.map((child) => (
                        <CategoryNode
                            key={child.id}
                            node={child}
                            selected={selected}
                            onToggle={onToggle}
                            depth={depth + 1}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

// ── Flat filtered category row ────────────────────────────────────────────────

const CategoryFlatRow = ({ cat, selected, onToggle }) => {
    const isSelected = selected.includes(cat.id);
    return (
        <div
            className={[
                "flex items-center gap-1.5 rounded-md px-2.5 py-[6px] cursor-pointer",
                "transition-colors duration-100",
                isSelected ? "bg-blue-50" : "hover:bg-gray-50",
            ].join(" ")}
            onClick={() => onToggle(cat.id)}
        >
            <span
                className={[
                    "h-[15px] w-[15px] shrink-0 rounded-[3px] border-[1.5px]",
                    "flex items-center justify-center transition-colors duration-100",
                    isSelected
                        ? "border-blue-600 bg-blue-600"
                        : "border-gray-200 bg-white",
                ].join(" ")}
            >
                {isSelected && (
                    <Check size={9} strokeWidth={3} className="text-white" />
                )}
            </span>
            <span
                className={[
                    "flex-1 min-w-0 truncate text-[12px]",
                    isSelected ? "font-medium text-blue-600" : "text-gray-600",
                ].join(" ")}
            >
                {cat.name}
            </span>
            {cat.count > 0 && (
                <span className="shrink-0 tabular-nums text-[10px] text-gray-300">
                    {cat.count}
                </span>
            )}
        </div>
    );
};

// ── Accordion header ──────────────────────────────────────────────────────────

const AccordionHeader = ({ icon: Icon, label, count, isOpen, onToggle }) => (
    <button
        type="button"
        onClick={onToggle}
        className={[
            "flex w-full items-center justify-between px-5 py-[13px] text-left",
            "transition-colors duration-[130ms] hover:bg-gray-50/80",
            isOpen ? "bg-gray-50/80" : "",
        ].join(" ")}
    >
        <span className="flex items-center gap-2 text-[12.5px] font-semibold text-blue-600 tracking-[-0.01em]">
            <Icon size={13} strokeWidth={1.8} className="opacity-80" />
            {label}
            {count > 0 && (
                <span className="rounded-full bg-blue-600 px-1.5 py-0.5 text-[10px] font-bold leading-none text-white">
                    {count}
                </span>
            )}
        </span>
        {isOpen ? (
            <ChevronDown size={14} className="shrink-0 text-gray-300" />
        ) : (
            <ChevronRight
                size={14}
                className="shrink-0 text-gray-300 rtl:rotate-180"
            />
        )}
    </button>
);

// ── Categories section ────────────────────────────────────────────────────────

const CategoriesSection = ({
    categories,
    selected,
    onToggle,
    isOpen,
    onOpenToggle,
}) => {
    const [filterQuery, setFilterQuery] = useState("");

    const tree = useMemo(() => buildTree(categories), [categories]);

    const filtered = useMemo(() => {
        if (!filterQuery.trim()) return null;
        const q = filterQuery.toLowerCase();
        return categories.filter((c) => c.name.toLowerCase().includes(q));
    }, [filterQuery, categories]);

    const isFiltering = filtered !== null;

    return (
        <div className="border-b border-gray-100">
            <AccordionHeader
                icon={Tag}
                label="Product Categories"
                count={selected.length}
                isOpen={isOpen}
                onToggle={onOpenToggle}
            />

            {isOpen && (
                <div className="px-4 pb-4">
                    <p className="mb-3 px-0.5 text-[11.5px] leading-relaxed text-gray-400">
                        Apply this rule to all products in the selected
                        categories.
                    </p>

                    {categories.length === 0 ? (
                        <p className="text-[12px] text-gray-400 px-1 py-1">
                            No WooCommerce categories found.
                        </p>
                    ) : (
                        <>
                            {/* Filter — only when list is large enough */}
                            {categories.length > 6 && (
                                <div className="relative mb-2.5">
                                    <Search
                                        size={11}
                                        className="absolute start-2.5 top-1/2 -translate-y-1/2 text-gray-300 pointer-events-none"
                                    />
                                    <input
                                        type="text"
                                        className={[
                                            "w-full rounded-lg border border-gray-200 bg-gray-50 py-[7px] ps-7 pe-7",
                                            "text-[12px] text-gray-800 placeholder:text-gray-400 outline-none",
                                            "transition-colors duration-150 focus:border-blue-400 focus:bg-white focus:ring-1 focus:ring-blue-500/20",
                                        ].join(" ")}
                                        placeholder="Filter categories…"
                                        value={filterQuery}
                                        onChange={(e) =>
                                            setFilterQuery(e.target.value)
                                        }
                                    />
                                    {filterQuery && (
                                        <button
                                            className="absolute end-2.5 top-1/2 -translate-y-1/2 text-gray-300 hover:text-gray-500"
                                            onClick={() => setFilterQuery("")}
                                        >
                                            <X size={11} />
                                        </button>
                                    )}
                                </div>
                            )}

                            <div className="max-h-[220px] overflow-y-auto rounded-lg border border-gray-100 fb-scrollbar-thin">
                                {isFiltering ? (
                                    filtered.length === 0 ? (
                                        <p className="px-3 py-3 text-[12px] text-gray-400">
                                            No categories match "{filterQuery}"
                                        </p>
                                    ) : (
                                        <div className="p-1">
                                            {filtered.map((cat) => (
                                                <CategoryFlatRow
                                                    key={cat.id}
                                                    cat={cat}
                                                    selected={selected}
                                                    onToggle={onToggle}
                                                />
                                            ))}
                                        </div>
                                    )
                                ) : (
                                    <div className="p-1">
                                        {tree.map((node) => (
                                            <CategoryNode
                                                key={node.id}
                                                node={node}
                                                selected={selected}
                                                onToggle={onToggle}
                                            />
                                        ))}
                                    </div>
                                )}
                            </div>

                            {selected.length > 0 && (
                                <p className="mt-2 px-0.5 text-[11px] font-medium text-blue-600">
                                    {selected.length}{" "}
                                    {selected.length === 1
                                        ? "category"
                                        : "categories"}{" "}
                                    selected
                                </p>
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

// ── Product search ────────────────────────────────────────────────────────────

const ProductSearch = ({ selectedProducts, onAdd }) => {
    const [query, setQuery] = useState("");
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const [dropdownPos, setDropdownPos] = useState({
        top: 0,
        left: 0,
        width: 0,
    });
    const timerRef = useRef(null);
    const wrapRef = useRef(null);
    const dropdownRef = useRef(null);

    // Compute portal position from the wrapper's bounding rect.
    // Runs on open and on any scroll / resize so it stays aligned.
    const updatePos = useCallback(() => {
        if (!wrapRef.current) return;
        const r = wrapRef.current.getBoundingClientRect();
        setDropdownPos({ top: r.bottom + 4, left: r.left, width: r.width });
    }, []);

    useEffect(() => {
        if (!open) return;
        updatePos();
        window.addEventListener("scroll", updatePos, {
            capture: true,
            passive: true,
        });
        window.addEventListener("resize", updatePos, { passive: true });
        return () => {
            window.removeEventListener("scroll", updatePos, { capture: true });
            window.removeEventListener("resize", updatePos);
        };
    }, [open, updatePos]);

    // Close on outside click — must check both the input wrapper AND the portal
    // dropdown (which lives in document.body, outside wrapRef).
    useEffect(() => {
        if (!open) return;
        const handler = (e) => {
            const inInput = wrapRef.current?.contains(e.target);
            const inDropdown = dropdownRef.current?.contains(e.target);
            if (!inInput && !inDropdown) setOpen(false);
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [open]);

    // Debounced search
    useEffect(() => {
        if (!query.trim()) {
            setResults([]);
            setOpen(false);
            return;
        }
        clearTimeout(timerRef.current);
        timerRef.current = setTimeout(async () => {
            setLoading(true);
            try {
                const data = await searchWcProducts({
                    search: query,
                    per_page: 15,
                });
                setResults(data.items ?? []);
                setOpen(true);
                updatePos();
            } catch {
                setResults([]);
            } finally {
                setLoading(false);
            }
        }, 350);
        return () => clearTimeout(timerRef.current);
    }, [query]);

    const handleSelect = (product) => {
        if (!selectedProducts.find((p) => p.id === product.id)) onAdd(product);
        setQuery("");
        setResults([]);
        setOpen(false);
    };

    // Portal dropdown — rendered at body level so no overflow clipping occurs.
    // Outer div carries `wpbean-pgs-wrapper` so PostCSS-scoped Tailwind classes apply.
    const dropdown =
        open &&
        createPortal(
            <div
                className="wpbean-pgs-wrapper"
                style={{
                    position: "fixed",
                    top: dropdownPos.top,
                    left: dropdownPos.left,
                    width: dropdownPos.width,
                    zIndex: 999999,
                }}
            >
                <div
                    ref={dropdownRef}
                    className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-[0_8px_32px_rgba(0,0,0,0.12)]"
                >
                    {results.length > 0 ? (
                        <div className="max-h-[256px] overflow-y-auto fb-scrollbar-thin divide-y divide-gray-50">
                            {results.map((product) => {
                                const added = !!selectedProducts.find(
                                    (p) => p.id === product.id,
                                );
                                return (
                                    <button
                                        key={product.id}
                                        className={[
                                            "flex w-full items-center gap-3 px-3 py-2.5 text-start transition-colors duration-100",
                                            added
                                                ? "cursor-default bg-gray-50/60"
                                                : "hover:bg-blue-50/50",
                                        ].join(" ")}
                                        onClick={() =>
                                            !added && handleSelect(product)
                                        }
                                        disabled={added}
                                    >
                                        {/* Thumbnail */}
                                        <div className="h-9 w-9 shrink-0 rounded-lg overflow-hidden border border-gray-100 bg-gray-50 flex items-center justify-center">
                                            {product.image ? (
                                                <img
                                                    src={product.image}
                                                    alt=""
                                                    style={{
                                                        width: "100%",
                                                        height: "100%",
                                                        objectFit: "cover",
                                                    }}
                                                />
                                            ) : (
                                                <Package
                                                    size={14}
                                                    className="text-gray-300"
                                                />
                                            )}
                                        </div>

                                        {/* Name + meta */}
                                        <div className="flex min-w-0 flex-1 flex-col gap-[3px]">
                                            <span
                                                className={[
                                                    "truncate text-[12.5px] font-medium leading-tight",
                                                    added
                                                        ? "text-gray-400"
                                                        : "text-gray-800",
                                                ].join(" ")}
                                            >
                                                {product.name}
                                            </span>
                                            <span className="flex items-center gap-1.5 text-[10.5px] leading-none text-gray-400">
                                                <span className="rounded bg-gray-100 px-1 py-px font-mono text-[9.5px] text-gray-500">
                                                    #{product.id}
                                                </span>
                                                {product.sku && (
                                                    <span className="text-gray-300">
                                                        SKU: {product.sku}
                                                    </span>
                                                )}
                                            </span>
                                        </div>

                                        {/* Added check */}
                                        {added ? (
                                            <Check
                                                size={13}
                                                className="shrink-0 text-blue-400"
                                            />
                                        ) : (
                                            <span className="shrink-0 rounded-md border border-blue-100 bg-blue-50 px-2 py-0.5 text-[10px] font-medium text-blue-500 opacity-0 transition-opacity duration-100 group-hover:opacity-100">
                                                Add
                                            </span>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    ) : loading ? (
                        <div className="flex items-center justify-center gap-2 px-4 py-5 text-[12px] text-gray-400">
                            <Loader2 size={13} className="animate-spin" />
                            Searching…
                        </div>
                    ) : (
                        query.trim() && (
                            <div className="px-4 py-5 text-center">
                                <p className="text-[12px] font-medium text-gray-500">
                                    No products found
                                </p>
                                <p className="mt-0.5 text-[11px] text-gray-400">
                                    Try a different name or SKU
                                </p>
                            </div>
                        )
                    )}
                </div>
            </div>,
            document.body,
        );

    return (
        <div ref={wrapRef} className="relative">
            <div className="relative flex items-center">
                {loading ? (
                    <Loader2
                        size={12}
                        className="absolute start-3 top-1/2 -translate-y-1/2 text-gray-300 animate-spin pointer-events-none"
                    />
                ) : (
                    <Search
                        size={12}
                        className="absolute start-3 top-1/2 -translate-y-1/2 text-gray-300 pointer-events-none"
                    />
                )}
                <input
                    type="text"
                    className={[
                        "w-full rounded-xl border border-gray-200 bg-gray-50 py-2.5 ps-8 pe-3",
                        "text-[12.5px] text-gray-800 placeholder:text-gray-400 outline-none",
                        "transition-all duration-150 focus:border-blue-400 focus:bg-white focus:ring-2 focus:ring-blue-500/10",
                    ].join(" ")}
                    placeholder="Search products by name or SKU…"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onFocus={() =>
                        results.length > 0 && (updatePos(), setOpen(true))
                    }
                />
                {query && (
                    <button
                        className="absolute end-2.5 top-1/2 -translate-y-1/2 text-gray-300 hover:text-gray-500"
                        onClick={() => {
                            setQuery("");
                            setResults([]);
                            setOpen(false);
                        }}
                    >
                        <X size={12} />
                    </button>
                )}
            </div>
            {dropdown}
        </div>
    );
};

// ── Product tag chip ──────────────────────────────────────────────────────────

const ProductTag = ({ product, onRemove }) => (
    <div className="flex items-center gap-2.5 rounded-xl border border-gray-100 bg-white px-2.5 py-2 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
        {product.image ? (
            <img
                src={product.image}
                alt=""
                className="h-9 w-9 shrink-0 rounded-lg object-cover border border-gray-100"
            />
        ) : (
            <div className="h-9 w-9 shrink-0 rounded-lg bg-gray-100 border border-gray-100 flex items-center justify-center text-gray-300">
                <Package size={14} />
            </div>
        )}
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            <span
                className="truncate text-[12.5px] font-medium text-gray-800 leading-tight"
                title={product.name}
            >
                {product.name}
            </span>
            <span className="text-[10.5px] text-gray-400 leading-none">
                ID: {product.id}
                {product.sku && (
                    <span className="ms-1.5 text-gray-300">
                        · SKU: {product.sku}
                    </span>
                )}
            </span>
        </div>
        <button
            onClick={() => onRemove(product.id)}
            className="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg text-gray-300 transition-colors duration-100 hover:bg-red-50 hover:text-red-400"
            aria-label={`Remove ${product.name}`}
        >
            <X size={11} strokeWidth={2.5} />
        </button>
    </div>
);

// ── Products section ──────────────────────────────────────────────────────────

const ProductsSection = ({
    selectedProducts,
    onAdd,
    onRemove,
    isOpen,
    onOpenToggle,
}) => (
    <div className="border-b border-gray-100">
        <AccordionHeader
            icon={Package}
            label="Specific Products"
            count={selectedProducts.length}
            isOpen={isOpen}
            onToggle={onOpenToggle}
        />

        {isOpen && (
            <div className="px-4 pb-4">
                <p className="mb-3 px-0.5 text-[11.5px] leading-relaxed text-gray-400">
                    Apply this rule directly to individual products.
                </p>

                <ProductSearch
                    selectedProducts={selectedProducts}
                    onAdd={onAdd}
                />

                {selectedProducts.length > 0 ? (
                    <div className="mt-2.5 flex flex-col gap-1.5">
                        {selectedProducts.map((p) => (
                            <ProductTag
                                key={p.id}
                                product={p}
                                onRemove={onRemove}
                            />
                        ))}
                    </div>
                ) : (
                    <p className="mt-2 px-0.5 text-[11px] text-gray-400">
                        No products added yet.
                    </p>
                )}
            </div>
        )}
    </div>
);

// ── Main panel ────────────────────────────────────────────────────────────────

const AssignmentsPanel = ({
    id,
    assignmentsSaveRef,
    onAssignmentsDirty,
    onAssignmentsClean,
}) => {
    const isNew = !id || id === 0;

    const [applyTo, setApplyTo] = useState("all");
    const [categories, setCategories] = useState([]);
    const [selectedCategories, setSelectedCategories] = useState([]);
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [loadingInit, setLoadingInit] = useState(true);
    const [catOpen, setCatOpen] = useState(true);
    const [prodOpen, setProdOpen] = useState(false);

    const savedSnapshotRef = useRef(null);

    // ── Snapshot helpers ──────────────────────────────────────────────────────

    const getCurrentSnapshot = useCallback(
        () =>
            JSON.stringify({
                applyTo,
                categories: [...selectedCategories].sort(),
                products: selectedProducts.map((p) => p.id).sort(),
            }),
        [applyTo, selectedCategories, selectedProducts],
    );

    // Notify parent whenever assignments dirty state changes.
    useEffect(() => {
        if (!savedSnapshotRef.current) return; // still loading — don't fire yet
        const dirty = savedSnapshotRef.current !== getCurrentSnapshot();
        if (dirty) {
            onAssignmentsDirty?.();
        } else {
            onAssignmentsClean?.();
        }
    }, [
        applyTo,
        selectedCategories,
        selectedProducts,
        getCurrentSnapshot,
        onAssignmentsDirty,
        onAssignmentsClean,
    ]);

    // ── Load ──────────────────────────────────────────────────────────────────

    useEffect(() => {
        let cancelled = false;

        (async () => {
            try {
                // Always load the category tree — even for new items.
                // Only fetch existing assignments when the row already exists.
                const [cats, assignments] = await Promise.all([
                    getWcCategories(),
                    isNew
                        ? Promise.resolve({
                              apply_to: "all",
                              categories: [],
                              products: [],
                          })
                        : getAssignments(id),
                ]);
                if (cancelled) return;

                setCategories(cats);
                setApplyTo(assignments.apply_to ?? "all");
                setSelectedCategories(assignments.categories ?? []);

                if (!isNew && assignments.products?.length) {
                    const data = await getWcProductsByIds(
                        assignments.products,
                    ).catch(() => ({ items: [] }));

                    const itemMap = Object.fromEntries(
                        (data.items ?? []).map((p) => [p.id, p]),
                    );
                    const resolved = assignments.products.map(
                        (pid) =>
                            itemMap[pid] ?? {
                                id: pid,
                                name: `Product #${pid}`,
                                sku: "",
                            },
                    );

                    if (!cancelled) setSelectedProducts(resolved);
                }

                if (!cancelled) {
                    savedSnapshotRef.current = JSON.stringify({
                        applyTo: assignments.apply_to ?? "all",
                        categories: [...(assignments.categories ?? [])].sort(),
                        products: [...(assignments.products ?? [])].sort(),
                    });
                    onAssignmentsClean?.(); // start clean after load
                }
            } catch {
                /* panel renders empty */
            } finally {
                if (!cancelled) setLoadingInit(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [id]);

    // ── Expose save — called by PluginSettingsApp.handleSave ─────────────────

    useEffect(() => {
        if (!assignmentsSaveRef) return;
        assignmentsSaveRef.current = async (finalId) => {
            if (!finalId || finalId === 0) return;
            await saveAssignments(
                finalId,
                applyTo,
                selectedCategories,
                selectedProducts.map((p) => p.id),
            );
            savedSnapshotRef.current = JSON.stringify({
                applyTo,
                categories: [...selectedCategories].sort(),
                products: selectedProducts.map((p) => p.id).sort(),
            });
            onAssignmentsClean?.(); // reset dirty after save
        };
    }, [
        applyTo,
        selectedCategories,
        selectedProducts,
        assignmentsSaveRef,
        onAssignmentsClean,
    ]);

    // ── Handlers ──────────────────────────────────────────────────────────────

    const toggleCategory = useCallback(
        (termId) =>
            setSelectedCategories((prev) =>
                prev.includes(termId)
                    ? prev.filter((c) => c !== termId)
                    : [...prev, termId],
            ),
        [],
    );

    const addProduct = useCallback(
        (p) => setSelectedProducts((prev) => [...prev, p]),
        [],
    );
    const removeProduct = useCallback(
        (pid) =>
            setSelectedProducts((prev) => prev.filter((p) => p.id !== pid)),
        [],
    );

    // ── Loading ───────────────────────────────────────────────────────────────

    if (loadingInit) {
        return (
            <div className="flex h-40 items-center justify-center gap-2 text-[12px] text-gray-300">
                <Loader2 size={16} className="animate-spin" />
                Loading assignments…
            </div>
        );
    }

    const showPickers = applyTo === "selected";
    const totalSelected = selectedCategories.length + selectedProducts.length;

    return (
        <div className="mx-auto max-w-[680px] pb-10 space-y-4">
            {/* ── Apply to selector ── */}
            <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
                <div className="border-b border-gray-100 bg-gray-50/60 px-5 py-[14px]">
                    <h3 className="text-[13px] font-semibold text-gray-700">
                        Apply Rule To
                    </h3>
                    <p className="mt-0.5 text-[11.5px] leading-relaxed text-gray-400">
                        Choose whether this zoom rule applies to all products or
                        only specific ones.
                    </p>
                </div>
                <div className="px-5 py-4">
                    <ButtonGroup
                        options={APPLY_TO_OPTIONS}
                        value={applyTo}
                        onChange={setApplyTo}
                    />
                    {applyTo === "all" && (
                        <p className="mt-3 text-[11.5px] text-gray-400 leading-relaxed">
                            This rule will be applied to{" "}
                            <span className="font-medium text-gray-600">
                                all products
                            </span>{" "}
                            in your store. To limit it, switch to "Selected
                            Only".
                        </p>
                    )}
                </div>
            </div>

            {/* ── Category & product pickers (conditional) ── */}
            {showPickers && (
                <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
                    <CategoriesSection
                        categories={categories}
                        selected={selectedCategories}
                        onToggle={toggleCategory}
                        isOpen={catOpen}
                        onOpenToggle={() => setCatOpen((v) => !v)}
                    />

                    <ProductsSection
                        selectedProducts={selectedProducts}
                        onAdd={addProduct}
                        onRemove={removeProduct}
                        isOpen={prodOpen}
                        onOpenToggle={() => setProdOpen((v) => !v)}
                    />

                    {totalSelected > 0 && (
                        <div className="m-4 flex items-start gap-2.5 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
                            <AlertTriangle
                                size={14}
                                className="mt-px shrink-0 text-amber-500"
                            />
                            <p className="text-[11.5px] leading-relaxed text-amber-700">
                                If a product matches multiple rules, the rule
                                with the highest ID (most recently created) will
                                be applied.
                            </p>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default AssignmentsPanel;
